// 🚨 Keep the css custom properties in these font family declarations.
// Other repos are relying on setting these properties themselves.
//
// Example with nextjs font optimization:
//    https://gitlab.com/zapier/team-explore/explore/-/blob/main/packages/nextplore/src/components/OptimizedFonts/OptimizedFonts.tsx#L53
const defaultFont =
  'var(--zds-typography-base, "Inter", Helvetica, arial, sans-serif)';
const headingFont =
  'var(--zds-typography-heading, "Degular", Helvetica, arial, sans-serif)';
const displayFont =
  'var(--zds-typography-heading-display, "Degular Display", Helvetica, arial, sans-serif)';

/**
 * These styles should exactly match those in the Zapier Design System.
 * That said, this list is not exhaustive for some properties, so not every
 * permutation (e.g. fontWeight) is covered.
 *
 * It is completely acceptable to do something like this in your styles:
 *
 * myTextElement: {
 *   ...Typography.sectionHeader3,
 *   fontWeight: 400,
 * },
 */

export const Typography = {
  // For use cases where styles are unique but the same
  // `fontFamily` should be used.
  base: {
    fontFamily: defaultFont,
  },
  heading: {
    fontFamily: headingFont,
  },

  // Page headers
  pageHeader1: {
    fontFamily: displayFont,
    fontSize: '82px',
    lineHeight: '84px',
    fontWeight: '700' as 'bold',
    letterSpacing: '1px',
  },
  pageHeader3: {
    fontFamily: displayFont,
    fontSize: '62px',
    lineHeight: '66px',
    fontWeight: '700' as 'bold',
    letterSpacing: '1px',
  },
  pageHeader4: {
    fontFamily: displayFont,
    fontSize: '52px',
    lineHeight: '60px',
    fontWeight: '700' as 'bold',
    letterSpacing: '1px',
  },
  pageHeader5: {
    fontFamily: headingFont,
    fontSize: '42px',
    lineHeight: '50px',
    fontWeight: '600' as 'bold',
    letterSpacing: '1px',
  },
  pageHeader7: {
    fontFamily: headingFont,
    fontSize: '34px',
    lineHeight: '40px',
    fontWeight: '600' as 'normal',
    letterSpacing: '1px',
  },
  pageHeader9: {
    fontFamily: headingFont,
    fontSize: '26px',
    lineHeight: '32px',
    fontWeight: '600' as 'normal',
    letterSpacing: '1px',
  },

  // Section headers
  sectionHeader1: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 700,
    letterSpacing: 'normal',
  },
  sectionHeader1Extrabold: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 800,
    letterSpacing: 'normal',
  },
  sectionHeader1Medium: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 500,
    letterSpacing: 'normal',
  },
  sectionHeader1Bold: {
    fontFamily: defaultFont,
    fontSize: 32,
    lineHeight: '35px',
    fontWeight: 700,
    letterSpacing: 'normal',
  },

  sectionHeader3: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 700,
    letterSpacing: 'normal',
  },
  sectionHeader3Extrabold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 800,
    letterSpacing: 'normal',
  },
  sectionHeader3Semibold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 600,
    letterSpacing: 'normal',
  },
  sectionHeader3Bold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 700,
    letterSpacing: 'normal',
  },

  // Subheaders
  subHeader1: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 400,
  },
  subHeader1Semibold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 600,
  },
  subHeader1Bold: {
    fontFamily: defaultFont,
    fontSize: 24,
    lineHeight: '30px',
    fontWeight: 700,
  },

  subHeader3: {
    fontFamily: defaultFont,
    fontSize: 22,
    lineHeight: '28px',
    fontWeight: 400,
  },
  subHeader3Medium: {
    fontFamily: headingFont,
    fontSize: 22,
    lineHeight: '28px',
    fontWeight: 500,
    letterSpacing: '1px',
  },
  subHeader3Bold: {
    fontFamily: headingFont,
    fontSize: 22,
    lineHeight: '28px',
    fontWeight: '600' as 'bold',
    letterSpacing: '1px',
  },

  subHeader5: {
    fontFamily: defaultFont,
    fontSize: 20,
    lineHeight: '26px',
    fontWeight: 400,
  },
  subHeader5Medium: {
    fontFamily: headingFont,
    fontSize: 20,
    lineHeight: '26px',
    fontWeight: 500,
    letterSpacing: '1px',
  },
  subHeader5Bold: {
    fontFamily: headingFont,
    fontSize: 20,
    lineHeight: '26px',
    fontWeight: '600' as 'bold',
    letterSpacing: '1px',
  },

  // Supertitles
  superTitle1: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  superTitle3: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  superTitle5: {
    fontFamily: defaultFont,
    fontSize: 10,
    lineHeight: '15px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },

  // Paragraphs
  paragraph1: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 400,
  },
  paragraph1Medium: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 500,
  },
  paragraph1Semibold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 600,
  },
  paragraph1Bold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '31px',
    fontWeight: 700,
  },
  paragraph1Short: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '24px',
    fontWeight: 400,
  },
  paragraph1ShortMedium: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '24px',
    fontWeight: 500,
  },
  paragraph1ShortSemibold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '24px',
    fontWeight: 600,
  },
  paragraph1ShortBold: {
    fontFamily: defaultFont,
    fontSize: 18,
    lineHeight: '24px',
    fontWeight: 700,
  },
  paragraph3: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 400,
  },
  paragraph3Medium: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
  },
  paragraph3Semibold: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
  },
  paragraph3Bold: {
    fontFamily: defaultFont,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 700,
  },

  // Small print
  smallPrint1: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
  },
  smallPrint1Medium: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500,
  },
  smallPrint1Semibold: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
  },
  smallPrint1Bold: {
    fontFamily: defaultFont,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
  },
  smallPrint3: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 400,
  },
  smallPrint3Semibold: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 600,
  },
  smallPrint3Bold: {
    fontFamily: defaultFont,
    fontSize: 12,
    lineHeight: '15px',
    fontWeight: 700,
  },
} as const;

export type TypographyName = keyof typeof Typography;

export const TypographyName = [
  'base',
  'heading',
  'pageHeader1',
  'pageHeader3',
  'pageHeader4',
  'pageHeader5',
  'pageHeader7',
  'pageHeader9',
  'sectionHeader1Medium',
  'sectionHeader1Bold',
  'sectionHeader3Semibold',
  'sectionHeader3Bold',
  'subHeader1',
  'subHeader1Semibold',
  'subHeader1Bold',
  'subHeader3Medium',
  'subHeader3Bold',
  'subHeader5Medium',
  'subHeader5Bold',
  'paragraph1',
  'paragraph1Medium',
  'paragraph1Semibold',
  'paragraph1Bold',
  'paragraph1Short',
  'paragraph1ShortMedium',
  'paragraph1ShortSemibold',
  'paragraph1ShortBold',
  'paragraph3',
  'paragraph3Medium',
  'paragraph3Semibold',
  'paragraph3Bold',
  'smallPrint1',
  'smallPrint1Medium',
  'smallPrint1Semibold',
  'smallPrint1Bold',
] as const;

export const TypographyPDL = (pdlCssVar: string, fallback: TypographyName) => {
  const letterSpacingFallback =
    (Typography[fallback] as any).letterSpacing ?? 'default';

  let letterSpacingPDL = '--intentionally-undefined';

  if (
    ['--zds-page-header', '--zds-section-header', '--zds-title'].includes(
      pdlCssVar
    )
  ) {
    letterSpacingPDL = '--zds-letter-spacing-header';
  }

  // subHeader fonts switch from Degular (w/ 1px line spacing) to Inter (no line spacing).
  // Without this, the PDL version of 'subHeaderX' looks stretched out.
  if (fallback.includes('subHeader')) letterSpacingPDL = '--zds-radius-none';

  const result = {
    font: `var(${pdlCssVar}, ${TypographyCSSFont[fallback]})`,
    letterSpacing: `var(${letterSpacingPDL}, ${letterSpacingFallback})`,
    textTransform:
      `var(--zds-radius-none, ${(Typography[fallback] as any).textTransform ?? 'none'})` as any, // "as any" to get around typechecker complaining about textTranform not being one of the predefined options
  };
  return result;
};

export const TypographyCSSFont: Record<TypographyName, string> = {
  base: `${defaultFont}`,
  heading: `${headingFont}`,

  superTitle1: `700 14px/20px ${defaultFont}`,
  superTitle3: `700 12px/15px ${defaultFont}`,
  superTitle5: `700 10px/15px ${defaultFont}`,

  pageHeader1: `700 82px/84px ${displayFont}`, // note: missing 1px letter-spacing!
  pageHeader3: `700 62px/66px ${displayFont}`, // note: missing 1px letter-spacing!
  pageHeader4: `700 52px/60px ${displayFont}`, // note: missing 1px letter-spacing!
  pageHeader5: `600 42px/50px ${headingFont}`, // note: missing 1px letter-spacing!
  pageHeader7: `600 34px/40px ${headingFont}`, // note: missing 1px letter-spacing!
  pageHeader9: `600 26px/32px ${headingFont}`, // note: missing 1px letter-spacing!

  sectionHeader1: `700 32px/35px ${defaultFont}`,
  sectionHeader1Medium: `500 32px/35px ${defaultFont}`,
  sectionHeader1Bold: `700 32px/35px ${defaultFont}`,
  sectionHeader1Extrabold: `800 32px/35px ${defaultFont}`,

  sectionHeader3: `700 24px/30px ${defaultFont}`,
  sectionHeader3Semibold: `600 24px/30px ${defaultFont}`,
  sectionHeader3Bold: `700 24px/30px ${defaultFont}`,
  sectionHeader3Extrabold: `800 24px/30px ${defaultFont}`,

  subHeader1: `400 24px/30px ${defaultFont}`,
  subHeader1Semibold: `600 24px/30px ${defaultFont}`,
  subHeader1Bold: `700 24px/30px ${defaultFont}`,

  subHeader3: `400 22px/28px ${headingFont}`,
  subHeader3Medium: `500 22px/28px ${headingFont}`, // note: missing 1px letter-spacing!
  subHeader3Bold: `600 22px/28px ${headingFont}`, // note: missing 1px letter-spacing!

  subHeader5: `400 20px/26px ${headingFont}`,
  subHeader5Medium: `500 20px/26px ${headingFont}`, // note: missing 1px letter-spacing!
  subHeader5Bold: `600 20px/26px ${headingFont}`, // note: missing 1px letter-spacing!

  paragraph1: `400 18px/31px ${defaultFont}`,
  paragraph1Medium: `500 18px/31px ${defaultFont}`,
  paragraph1Semibold: `600 18px/31px ${defaultFont}`,
  paragraph1Bold: `700 18px/31px ${defaultFont}`,

  paragraph1Short: `400 18px/24px ${defaultFont}`,
  paragraph1ShortMedium: `500 18px/24px ${defaultFont}`,
  paragraph1ShortSemibold: `600 18px/24px ${defaultFont}`,
  paragraph1ShortBold: `700 18px/24px ${defaultFont}`,

  paragraph3: `400 16px/24px ${defaultFont}`,
  paragraph3Medium: `500 16px/24px ${defaultFont}`,
  paragraph3Semibold: `600 16px/24px ${defaultFont}`,
  paragraph3Bold: `700 16px/24px ${defaultFont}`,

  smallPrint1: `400 14px/20px ${defaultFont}`,
  smallPrint1Medium: `500 14px/20px ${defaultFont}`,
  smallPrint1Semibold: `600 14px/20px ${defaultFont}`,
  smallPrint1Bold: `700 14px/20px ${defaultFont}`,

  smallPrint3: `400 12px/15px ${defaultFont}`,
  smallPrint3Semibold: `600 12px/15px ${defaultFont}`,
  smallPrint3Bold: `700 12px/15px ${defaultFont}`,
};

export const TypographyLegacyPDLMap: Record<TypographyName, string> = {
  base: '--zds-inter',
  heading: '--zds-degular',

  pageHeader1: '--zds-page-header',
  pageHeader3: '--zds-page-header',
  pageHeader4: '--zds-page-header',
  pageHeader5: '--zds-page-header',
  pageHeader7: '--zds-page-header',
  pageHeader9: '--zds-section-header',

  sectionHeader1: '--zds-section-header',
  sectionHeader1Medium: '--zds-section-header',
  sectionHeader1Bold: '--zds-section-header',
  sectionHeader1Extrabold: '--zds-section-header',

  sectionHeader3: '--zds-section-header',
  sectionHeader3Semibold: '--zds-section-header',
  sectionHeader3Bold: '--zds-section-header',
  sectionHeader3Extrabold: '--zds-section-header',

  subHeader1: '--zds-paragraph-header-1',
  subHeader1Semibold: '--zds-paragraph-header-3',
  subHeader1Bold: '--zds-paragraph-header-4',

  subHeader3: '--zds-paragraph-header-1',
  subHeader3Medium: '--zds-paragraph-header-2',
  subHeader3Bold: '--zds-paragraph-header-3',

  subHeader5: '--zds-paragraph-header-1',
  subHeader5Medium: '--zds-paragraph-header-2',
  subHeader5Bold: '--zds-paragraph-header-3',

  paragraph1: '--zds-body-1',
  paragraph1Medium: '--zds-body-2',
  paragraph1Semibold: '--zds-body-3',
  paragraph1Bold: '--zds-body-4',

  paragraph1Short: '--zds-body-1',
  paragraph1ShortMedium: '--zds-body-2',
  paragraph1ShortSemibold: '--zds-body-3',
  paragraph1ShortBold: '--zds-body-4',

  paragraph3: '--zds-body-1',
  paragraph3Medium: '--zds-body-2',
  paragraph3Semibold: '--zds-body-3',
  paragraph3Bold: '--zds-body-4',

  smallPrint1: '--zds-small-print-1',
  smallPrint1Medium: '--zds-small-print-2',
  smallPrint1Semibold: '--zds-small-print-3',
  smallPrint1Bold: '--zds-small-print-4',

  smallPrint3: '--zds-small-print-1',
  smallPrint3Semibold: '--zds-small-print-2',
  smallPrint3Bold: '--zds-small-print-3',

  superTitle1: '--zds-title',
  superTitle3: '--zds-title',
  superTitle5: '--zds-title',
};
